// DTO de D2 — Resumen Semanal del directivo.
//
//   - GET /api/directivo/resumen-semanal?semana=YYYY-MM-DD (lunes; default sem actual)
//
// Cadencia semanal (~30 min, reunión con coordinadores): comparativo cross-planta
// de la semana, gráfico cumplimiento por planta (programa vs meta interna),
// tabla comparativa y eventos destacados (derivados de los datos). SOLO LECTURA.
//
// Reutiliza estadoPlantaSchema de directivo_dashboard. El export PDF "para junta"
// está diferido a F1.5 (no hay endpoint).
import { z } from 'zod';
import { estadoPlantaSchema } from './directivo_dashboard.js';
export const directivoResumenSemanalQuerySchema = z.object({
    /** Lunes de la semana a resumir. Si se omite, usa la semana en curso. */
    semana: z.string().date().optional(),
});
// =============================================================================
// KPIs (4 cards del header)
// =============================================================================
export const resumenSemanalKpisSchema = z.object({
    cumplimiento_consolidado: z.object({
        valor_pct: z.number().min(0).nullable(),
        /** Delta en pp vs semana anterior. */
        delta_pp: z.number().nullable(),
    }),
    produccion_total: z.object({
        cantidad: z.number().min(0),
        plantas: z.number().int().min(0),
        lineas: z.number().int().min(0),
    }),
    plantas_alerta: z.object({
        count: z.number().int().min(0),
        total: z.number().int().min(0),
        /** Planta + línea más crítica de la semana (subtítulo). null si ninguna. */
        peor_planta_nombre: z.string().nullable(),
        peor_linea_label: z.string().nullable(),
    }),
    cliente_principal: z.object({
        nombre: z.string(),
        /** % del volumen total (real) que representa el cliente principal. */
        share_pct: z.number().min(0).nullable(),
        cantidad: z.number().min(0),
    }),
});
// =============================================================================
// Por planta (gráfico de barras + tabla comparativa)
// =============================================================================
export const resumenPlantaSemanaSchema = z.object({
    planta_id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
    departamento: z.string().nullable(),
    estado: estadoPlantaSchema,
    /** Cumplimiento vs programa Alpina (barra "Programa"). */
    cumpl_programa_pct: z.number().min(0).nullable(),
    /** Cumplimiento vs meta interna / capacidad (barra "Meta interna"). */
    cumpl_meta_pct: z.number().min(0).nullable(),
    producido: z.number().min(0),
    horas_paro: z.number().min(0),
});
// =============================================================================
// Eventos destacados (derivados de los datos — heurísticos)
// =============================================================================
export const eventoSemanaDestacadoSchema = z.object({
    planta_nombre: z.string(),
    planta_codigo: z.string(),
    estado: estadoPlantaSchema,
    texto: z.string(),
});
// =============================================================================
// Tendencia de cumplimiento semanal (multi-semana, por planta) — filtrable
// =============================================================================
// Serie de cumplimiento vs programa por (semana, planta) para las últimas N
// semanas terminando en la semana seleccionada. Cada planta es una línea; el FE
// permite aislar una planta. `semana_iso` es el lunes de esa semana (YYYY-MM-DD).
export const tendenciaSemanalSchema = z.object({
    semanas: z.array(z.object({ semana_iso: z.string().date(), label: z.string() })),
    plantas: z.array(z.object({ planta_id: z.string().uuid(), nombre: z.string() })),
    puntos: z.array(z.object({
        semana_iso: z.string().date(),
        planta_id: z.string().uuid(),
        cumplimiento_pct: z.number().min(0).nullable(),
    })),
});
// =============================================================================
// Response
// =============================================================================
export const directivoResumenSemanalSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    semana: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
        numero_iso: z.number().int().min(1).max(53),
    }),
    kpis: resumenSemanalKpisSchema,
    por_planta: z.array(resumenPlantaSemanaSchema),
    tendencia_semanal: tendenciaSemanalSchema,
    eventos: z.array(eventoSemanaDestacadoSchema),
    actualizado_at: z.string().datetime(),
});
//# sourceMappingURL=directivo_resumen_semanal.js.map